import sectionList from './sections.json';

/**
 * In-app help ("Information and How-Tos") — the content model.
 *
 * Every page is a markdown file at src/help/<section>/<page>.md, bundled at
 * build time (import.meta.glob ?raw), so help ships with the app and needs no
 * API or session. The same files are published to the GitHub wiki by
 * .github/workflows/publish-docs.yml (scripts/help-wiki-bundle.mjs), so the
 * wiki and the app share one source. The page's URL is its file name:
 * users/sign-in-and-out.md is /help/users/sign-in-and-out.
 *
 * A page starts with a small front-matter block, then a `#` title:
 *
 *   ---
 *   order: 3
 *   summary: One line, shown in the page lists.
 *   ---
 *   # Sign in and sign out
 */

export interface HelpSectionInfo {
  id: string;
  title: string;
  description: string;
}

export interface HelpPage {
  section: string;
  slug: string;
  title: string;
  summary: string;
  order: number;
  /** Markdown, front matter removed (the `#` title included). */
  body: string;
}

export interface HelpSection extends HelpSectionInfo {
  pages: HelpPage[];
}

const FRONT_MATTER = /^---\n([\s\S]*?)\n---(?:\n|$)/;
// The file name becomes a URL segment and a wiki page name.
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Splits `key: value` front matter from the markdown body. */
export function parseHelpFile(raw: string): { meta: Record<string, string>; body: string } {
  const text = raw.replace(/\r\n?/g, '\n');
  const match = FRONT_MATTER.exec(text);
  if (!match) return { meta: {}, body: text.trim() };

  const meta: Record<string, string> = {};
  for (const line of match[1].split('\n')) {
    const colon = line.indexOf(':');
    if (colon > 0) meta[line.slice(0, colon).trim()] = line.slice(colon + 1).trim();
  }
  return { meta, body: text.slice(match[0].length).trim() };
}

function titleOf(body: string, slug: string): string {
  const heading = /^#\s+(.+)$/m.exec(body);
  return heading ? heading[1].trim() : slug.replace(/-/g, ' ');
}

/** Groups raw files (keyed by path) into the known sections, in page order. */
export function buildHelp(
  files: Record<string, string>,
  sections: HelpSectionInfo[] = sectionList
): HelpSection[] {
  const bySection = new Map<string, HelpSection>(
    sections.map((section) => [section.id, { ...section, pages: [] }])
  );

  for (const [path, raw] of Object.entries(files)) {
    const match = /([^/]+)\/([^/]+)\.md$/.exec(path);
    if (!match) continue;
    const [, sectionId, slug] = match;
    const section = bySection.get(sectionId);
    if (!section || !SLUG.test(slug)) continue;

    const { meta, body } = parseHelpFile(raw);
    section.pages.push({
      section: sectionId,
      slug,
      title: titleOf(body, slug),
      summary: meta.summary ?? '',
      order: Number(meta.order) || Number.MAX_SAFE_INTEGER,
      body,
    });
  }

  const result = [...bySection.values()];
  for (const section of result) {
    section.pages.sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  }
  return result;
}

const files = import.meta.glob<string>('./*/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

export const helpSections: HelpSection[] = buildHelp(files);

export function findSection(sectionId: string | undefined): HelpSection | undefined {
  return helpSections.find((section) => section.id === sectionId);
}

export function findPage(
  sectionId: string | undefined,
  slug: string | undefined
): HelpPage | undefined {
  return findSection(sectionId)?.pages.find((page) => page.slug === slug);
}
